import { useState, useCallback } from 'react';

/**
 * Hook for managing map modal state and operations
 * @param {Array} allEvents - All events from parent component
 */
export function useMapModal(allEvents = []) {
  const [mapModalOpen, setMapModalOpen] = useState(false);
  const [mapModalData, setMapModalData] = useState(null);
  const [mapModalContext, setMapModalContext] = useState(null); // Store context for refreshing

  /**
   * Open map for a person's events
   */
  const openPersonMap = useCallback((event) => {
    if (!event.location) return;

    const personName = event.metadata?.personName || event.userId;
    const personImage = event.metadata?.personImage;

    console.log(`📍 [Map Modal] Opening person map for "${personName}" - using cached ${allEvents.length} events (NO API CALL)`);

    // Store context for refreshing
    setMapModalContext({
      type: 'person',
      personName,
      personImage,
      event
    });

    // Use cached events - no API call!
    const personEvents = allEvents.filter(e =>
      e.location &&
      (e.metadata?.personName === personName || e.userId === personName)
    );

    personEvents.sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));

    console.log(`📍 [Map Modal] Found ${personEvents.length} events for "${personName}"`);

    setMapModalData({
      events: personEvents.length > 1 ? personEvents : [event],
      personName,
      personImage
    });
    setMapModalOpen(true);
  }, [allEvents]);

  /**
   * Open map for group match events
   */
  const openGroupMap = useCallback((groupEvent) => {
    if (!groupEvent.location) return;

    const groupEventId = groupEvent.eventId;
    const personNames = groupEvent.metadata?.personNames || [];
    const capturedImageUrl = groupEvent.metadata?.capturedImageUrl;

    // Store context for refreshing
    setMapModalContext({
      type: 'group',
      groupEventId,
      personNames,
      capturedImageUrl,
      groupEvent
    });

    // Use cached events - no API call!
    const groupMatchEvents = allEvents.filter(e => {
      if (e.eventType !== 'match' || !e.location) return false;
      return e.metadata?.groupEventId === groupEventId;
    });

    groupMatchEvents.sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());

    setMapModalData({
      events: groupMatchEvents.length > 0 ? groupMatchEvents : [groupEvent],
      personName: `Group: ${personNames.slice(0, 2).join(', ')}${personNames.length > 2 ? ` +${personNames.length - 2}` : ''}`,
      personImage: null
    });
    setMapModalOpen(true);
  }, [allEvents]);

  /**
   * Open map for assignment's match events
   */
  const openAssignmentMap = useCallback((assignmentEvent) => {
    // Get the assignment event ID
    const assignmentEventId = assignmentEvent.eventType === 'assignment'
      ? assignmentEvent.eventId
      : assignmentEvent.metadata?.assignmentEventId;

    console.log('[useMapModal] openAssignmentMap called with:', assignmentEvent);
    console.log('[useMapModal] Assignment ID:', assignmentEventId);
    console.log('[useMapModal] All events count:', allEvents.length);

    if (!assignmentEventId) {
      console.log('[useMapModal] No assignment ID found, returning');
      return;
    }

    // Get assignment description
    const assignmentDescription = assignmentEvent.eventType === 'assignment'
      ? assignmentEvent.description
      : 'Assignment';

    // Store context for refreshing
    setMapModalContext({
      type: 'assignment',
      assignmentEventId,
      assignmentDescription,
      assignmentEvent
    });

    // Use cached events - no API call!
    const matchEvents = allEvents.filter(e => {
      if (e.eventType !== 'match' || !e.location) return false;
      return e.metadata?.assignmentEventId === assignmentEventId;
    });

    console.log('[useMapModal] Found match events:', matchEvents.length);

    if (matchEvents.length === 0) {
      console.log('[useMapModal] No match events found for assignment, not opening map');
      return;
    }

    matchEvents.sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());

    setMapModalData({
      events: matchEvents,
      personName: assignmentDescription,  // Use assignment description as title
      personImage: null
    });
    setMapModalOpen(true);
  }, [allEvents]);

  /**
   * Refresh map data based on current context
   * Called when new events arrive
   * Uses cached events from React Query - will automatically update when cache refreshes
   */
  const refreshMapData = useCallback(() => {
    if (!mapModalOpen || !mapModalContext) return;

    if (mapModalContext.type === 'person') {
      const { personName, personImage, event } = mapModalContext;
      const personEvents = allEvents.filter(e =>
        e.location &&
        (e.metadata?.personName === personName || e.userId === personName)
      );
      personEvents.sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));

      setMapModalData({
        events: personEvents.length > 1 ? personEvents : [event],
        personName,
        personImage
      });
    } else if (mapModalContext.type === 'assignment') {
      const { assignmentEventId, assignmentDescription } = mapModalContext;
      const matchEvents = allEvents.filter(e => {
        if (e.eventType !== 'match' || !e.location) return false;
        return e.metadata?.assignmentEventId === assignmentEventId;
      });

      if (matchEvents.length > 0) {
        matchEvents.sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());
        setMapModalData({
          events: matchEvents,
          personName: assignmentDescription,
          personImage: null
        });
      }
    } else if (mapModalContext.type === 'group') {
      const { groupEventId, personNames, groupEvent } = mapModalContext;
      const groupMatchEvents = allEvents.filter(e => {
        if (e.eventType !== 'match' || !e.location) return false;
        return e.metadata?.groupEventId === groupEventId;
      });

      groupMatchEvents.sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());

      setMapModalData({
        events: groupMatchEvents.length > 0 ? groupMatchEvents : [groupEvent],
        personName: `Group: ${personNames.slice(0, 2).join(', ')}${personNames.length > 2 ? ` +${personNames.length - 2}` : ''}`,
        personImage: null
      });
    }
  }, [mapModalOpen, mapModalContext, allEvents]);

  const closeMap = () => {
    setMapModalOpen(false);
    setMapModalData(null);
    setMapModalContext(null);
  };

  return {
    mapModalOpen,
    mapModalData,
    openPersonMap,
    openGroupMap,
    openAssignmentMap,
    closeMap,
    refreshMapData
  };
}
